import {
  Alert,
  Avatar,
  Button,
  Divider,
  Group,
  List,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core"
import { match } from "ts-pattern"
import { AbsentApprovalStatusIcon } from "../absent-approval-status-icon"
import {
  UserName,
  UserPersonNameText,
} from "../../../blocks/persons-and-users/User"
import { IconAlertCircle, IconPlus } from "@tabler/icons-react"
import dayjs from "dayjs"
import { SuspenseWithErrorBoundary } from "../../../-components/suspense"
import { UserSingleSelect } from "../../../-components/AbsenceUserWithEmailInfoSelect"
import { Form } from "../../../-components/typed-form"
import { SubmitButton } from "../../../-components/Buttons"
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../../utils/form-submission-notifications"
import { DottedMenu } from "../../../-components/dotted-menu"
import { Tuple } from "../../../@client/System"
import { Link } from "@tanstack/react-router"
import { Trans, useTranslation } from "react-i18next"
import { useTranslations } from "../../../utils/use-t"
import { useSuspenseQueryGet } from "../../../typed-client/hooks/use-suspense-query-get"
import { useCommand } from "../../../typed-client/hooks/use-command"
import { useInvalidate } from "../../../typed-client/hooks/use-invalidate"
import { SimpleRoleWithProduction } from "../../../@client/Theasoft_ApiServer_Roles"
import { NodeId } from "../../../@client/Theasoft_Absences_Workflow"
import {
  Approval,
  ApprovalAddedReason,
  ApprovalResult,
  UserId,
} from "../../../@client/Absents"
import { ApprovalAndWorkflowNode } from "../../../@client/Theasoft_Absences"
import { PersonName } from "@components/PersonName"
import { useBatchedSimpleUser } from "@/-dataloaders/simple-users"

function RenderRoleInternal({
  roleTypeId,
  children,
}: {
  roleTypeId: number
  children: (data: SimpleRoleWithProduction) => React.ReactNode
}) {
  const data = useSuspenseQueryGet(
    "/api/Role/GetSimpleRoleWithProduction",
    {
      roleId: roleTypeId,
    },
    {
      gcTime: 10000,
      staleTime: 10000,
    }
  )
  return children(data.data)
}

export function RenderRoleType({
  roleTypeId,
  children,
}: {
  roleTypeId: number
  children: (data: SimpleRoleWithProduction) => React.ReactNode
}) {
  return (
    <SuspenseWithErrorBoundary loadingProps={{ type: "dots" }}>
      <RenderRoleInternal roleTypeId={roleTypeId} children={children} />
    </SuspenseWithErrorBoundary>
  )
}

export function ApprovalResultText({
  isNodeActive,
  value,
  workflowFinished,
}: {
  workflowFinished: boolean
  isNodeActive: boolean
  value: ApprovalResult
}) {
  const ts = useTranslations()

  return match(value)
    .with({ Case: "Approved" }, () => <Text>{ts.appServer.t("Approved")}</Text>)
    .with({ Case: "Rejected" }, () => <Text>{ts.appServer.t("Rejected")}</Text>)
    .with({ Case: "Pending" }, () =>
      workflowFinished ? (
        <Text>{ts.appServer.t("Skipped")}</Text>
      ) : isNodeActive ? (
        <Text>
          {ts.absences.t("Pending")} & {ts.appServer.t("Active")}
        </Text>
      ) : (
        <Text>{ts.absences.t("Pending")}</Text>
      )
    )
    .exhaustive()
}

export function RenderApprovalResult({ approval }: { approval: Approval }) {
  const { data } = useBatchedSimpleUser(approval.userId)
  const ts = useTranslations()
  const langKey = match(approval.result)
    .with({ Case: "Approved" }, () => "ApproverApproved" as const)
    .with({ Case: "Rejected" }, () => "ApproverRejected" as const)
    .otherwise((x) => x.Case)
  if (!data) {
    return <Text c="red">User not found</Text>
  }
  const name = data.userName

  return (
    <Group mt={2} gap="xs">
      <Trans
        i18nKey={langKey}
        t={ts.absences.t}
        shouldUnescape={true}
        values={{
          approver: name,
          date: approval.reviewedAt
            ? dayjs(approval.reviewedAt).format("ddd L")
            : "N/A",
        }}
      />
      {approval.comment !== null ? (
        <Text c="orange">{approval.comment}</Text>
      ) : null}
    </Group>
  )
}

export function ApproverName({ approval }: { approval: Approval }) {
  return (
    <>
      <Tooltip label={<ApprovalAddedReasonTooltip reason={approval.reason} />}>
        <div>
          <UserPersonNameText id={approval.userId} />
        </div>
      </Tooltip>
      {match(approval.reason)
        .with({ Case: "PersonHasRoleInProduction" }, ({ Fields }) => (
          <RenderRoleType {...Fields}>
            {(data) => ` (${data.roleName})`}
          </RenderRoleType>
        ))
        .otherwise(() => null)}
    </>
  )
}

export function ApprovalAddedReasonTooltip({
  reason,
}: {
  reason: ApprovalAddedReason
}) {
  return (
    <Text>
      {match(reason)
        .with({ Case: "PersonHasRoleInProduction" }, ({ Fields }) => (
          <RenderRoleType {...Fields}>
            {(data) =>
              `Person hat Rolle ${data.roleName} in der Produktion ${data.productionTitle}`
            }
          </RenderRoleType>
        ))
        .with(
          { Case: "AdminFallbackNoPersonsWithUsers" },
          () =>
            "Da keine passende Person mit Benutzerkonto gefunden wurden, wurde dieser Admin hinterlegt"
        )
        .with(
          { Case: "AdminFallback" },
          () =>
            "Da keine passende Person mit Benutzerkonto gefunden wurden, wurde dieser Admin hinterlegt"
        )
        .with(
          { Case: "StaticalyDefined" },
          () => "In der Konfiguration festgelegt"
        )
        .with({ Case: "StaticalyDefinedByGroup" }, (x) => (
          <Text>
            Gruppe <UserName id={x.Fields} /> in Konfiguration festgelegt
          </Text>
        ))
        .with({ Case: "ManuallyAdded" }, () => "Manuell hinzugefügt")
        .with(
          { Case: "PersonHasDepartmentFunction" },
          () => "Person hat Funktion in Abteilung"
        )
        .exhaustive()}
    </Text>
  )
}

function RemoveApprover({
  nodeId,
  userId,
  onRemoved,
  leaveRequestId,
}: {
  userId: UserId
  nodeId: NodeId
  onRemoved: () => void
  leaveRequestId: number
}) {
  const invalidate = useInvalidate()
  const { t } = useTranslation("app_server")
  const command = useCommand({
    url: "/api/Absence/Command",
    options: {
      onSuccess: () => {
        invalidate("/api/Absence/GetApprovalRequestForReview")
        showSuccessNotification("Person entfernt")
        onRemoved()
      },
      onError: showErrorNotification,
    },
  })
  return (
    <DottedMenu
      items={[
        {
          children: t("Remove"),
          onClick: () =>
            command.mutate({
              streamId: leaveRequestId,
              command: { Case: "RemoveApprover", Fields: { nodeId, userId } },
            }),
        },
      ]}
    />
  )
}

function AddApprover({
  nodeId,
  onAdded,
  leaveRequestId,
}: {
  nodeId: NodeId
  onAdded: () => void
  leaveRequestId: number
}) {
  const [adding, setAdding] = React.useState(false)
  const invalidate = useInvalidate()
  const { t } = useTranslation("app_server")
  return (
    <Group mt={"xs"} flex="1" justify="end">
      {adding ? (
        <Form<{ userId: UserId }, "/api/Absence/Command">
          url="/api/Absence/Command"
          transform={(v) => ({
            streamId: leaveRequestId,
            command: {
              Case: "AddApprover",
              Fields: { nodeId, userId: v.userId },
            },
          })}
          options={{
            onSuccess: () => {
              invalidate("/api/Absence/GetApprovalRequestForReview")
              showSuccessNotification("Person hinzugefügt")
              setAdding(false)
              onAdded()
            },
          }}
        >
          {(f, _, m) => (
            <Group>
              <UserSingleSelect
                disableMantineFilter
                miw={260}
                style={{ flex: 1 }}
                required
                defaultDropdownOpened
                searchable
                clearable
                autoFocus
                size="xs"
                {...f.getInputProps(_.userId._PATH_)}
              />
              <SubmitButton loading={m.isPending} variant="default" size="xs">
                {t("Add")}
              </SubmitButton>
            </Group>
          )}
        </Form>
      ) : (
        <Button
          variant="subtle"
          mx={0}
          px={6}
          color="gray"
          size="xs"
          onClick={() => setAdding(true)}
        >
          <IconPlus size={14} />
        </Button>
      )}
    </Group>
  )
}

export function RenderPendingApprovalCard({
  node: {
    approvalNode,
    workflowNode: { name, status },
  },
  canResolvePersonUsers,
  isWorkflowFinished,
  canChangeApprovers,
  onChanged,
  leaveRequestId,
}: {
  node: ApprovalAndWorkflowNode
  canResolvePersonUsers: boolean
  isWorkflowFinished: boolean
  canChangeApprovers: boolean
  onChanged: () => void
  leaveRequestId: number
}) {
  const details = match(approvalNode.details)
    .with({ Case: "Approval" }, (x) => x)
    .otherwise((x) => {
      throw new Error(`Unknown error encountered (node type ${x.Case}). Sorry.`)
    })

  return (
    <>
      {name ? (
        <Text size="xl" fw={500}>
          {name}
        </Text>
      ) : null}

      <Group>
        <Text>Status: </Text>
        <ApprovalResultText
          workflowFinished={isWorkflowFinished}
          isNodeActive={status.Case === "Active"}
          value={details.Fields.result}
        />
      </Group>
      <Divider my="md" />
      <Stack px={0} style={{ flex: 1 }} gap={"md"}>
        {details.Fields.approvals.map((approval, i) => (
          <Group mt={2} gap="xs" key={i}>
            <AbsentApprovalStatusIcon
              size="md"
              variant="transparent"
              status={approval.result}
              showPending={true}
            />
            <Avatar size="sm" src={null} />
            <Group style={{ flex: 1 }} justify="space-between">
              <ApproverName approval={approval} />
              {canChangeApprovers ? (
                <RemoveApprover
                  userId={approval.userId}
                  nodeId={approvalNode.nodeId}
                  onRemoved={onChanged}
                  leaveRequestId={leaveRequestId}
                />
              ) : null}
            </Group>
          </Group>
        ))}
        {canChangeApprovers ? (
          <SuspenseWithErrorBoundary>
            <AddApprover
              nodeId={approvalNode.nodeId}
              onAdded={onChanged}
              leaveRequestId={leaveRequestId}
            />
          </SuspenseWithErrorBoundary>
        ) : null}
        {details.Fields.approversWithoutUser.length > 0 &&
        canResolvePersonUsers ? (
          <ApproversWithoutUsersInfo
            approversWithoutUser={details.Fields.approversWithoutUser}
          />
        ) : null}
      </Stack>
    </>
  )
}

// function RenderApprovalReason({ reason }: { reason: ApprovalAddedReason }) {
//   const ts = useTranslations()
//   return match(reason)
//     .with({ Case: "AdminFallbackNoPersonsWithUsers" }, () => (
//       <Text>{ts.absences.t("ApprovalReason_AdminFallback")}</Text>
//     ))
//     .with({ Case: "AdminFallback" }, (x) => (
//       <Text>{ts.absences.t("ApprovalReason_AdminFallback")}</Text>
//     ))
//     .with({ Case: "PersonHasRoleInProduction" }, (x) => (
//       <Text>
//         <RenderRoleType {...x.Fields}>
//           {(data) => (
//             <p>
//               <Trans
//                 i18nKey={"ApprovalReason_ProductionRole"}
//                 t={ts.absences.t}
//                 values={{
//                   role: data.roleName,
//                   production: data.productionTitle,
//                 }}
//                 components={[
//                   <strong>dummyChild</strong>,
//                   <strong>dummyChild</strong>,
//                 ]}
//               />
//             </p>
//           )}
//         </RenderRoleType>
//       </Text>
//     ))
//     .with({ Case: "StaticalyDefined" }, () => (
//       <Text>{ts.absences.t("ApprovalReason_StaticallyDefined")}</Text>
//     ))
//     .with({ Case: "StaticalyDefinedByGroup" }, (x) => (
//       <Text>
//         <RenderUser
//           id={x.Fields}
//           render={(u) => (
//             <Trans
//               i18nKey={"ApprovalReason_StaticallyGroupDefined"}
//               t={ts.absences.t}
//               values={{
//                 group: u.userName,
//               }}
//               components={[<strong>dummyChild</strong>]}
//             />
//           )}
//         />
//       </Text>
//     ))
//     .with({ Case: "ManuallyAdded" }, (byUser) => (
//       <Text>
//         Sie wurden von <UserName id={byUser.Fields} /> in diesem
//         Genehmigungsschritt für die Genehmigung hinzugefügt.
//       </Text>
//     ))
//     .with({ Case: "PersonHasDepartmentFunction" }, (x) => (
//       <Text>
//         Sie sind auf Grund ihrer Funktion in der Abteilung für die Genehmigung
//         ausgewählt
//       </Text>
//     ))
//     .exhaustive()
// }

export function ApproversWithoutUsersInfo({
  approversWithoutUser,
}: {
  approversWithoutUser: Tuple<number, ApprovalAddedReason>[]
}) {
  const ts = useTranslations()
  return (
    <Alert mt="xs" icon={<IconAlertCircle />} color="violet">
      <Text span>{ts.absences.t("ApproversWithoutUser")}</Text>{" "}
      <List>
        {approversWithoutUser.map((approver) => (
          <List.Item key={approver[0]}>
            <div>
              <Text span>
                <PersonName personId={approver[0]} />
              </Text>
              <Link
                to={"/app/admin-settings/users/new/user-for-person"}
                search={{ personId: approver[0] }}
              >
                <Text span>{ts.appServer.t("CreateUser")}</Text>
              </Link>
            </div>
          </List.Item>
        ))}
      </List>
    </Alert>
  )
}
